import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

// Firebase imports (tree-shakeable)
import { initializeApp, FirebaseApp, getApps } from 'firebase/app';
import { getAuth, signInWithPopup, OAuthProvider, Auth, signOut } from 'firebase/auth';

@Injectable({ providedIn: 'root' })
export class AppleAuthService {

  private firebaseApp: FirebaseApp | null = null;
  private firebaseAuth: Auth | null = null;

  constructor() {}

  /**
   * Inicializa Firebase App (reutiliza instancia existente do GoogleAuthService)
   */
  private initFirebase(): void {
    if (this.firebaseApp) return;

    const apps = getApps();
    if (apps.length > 0) {
      this.firebaseApp = apps[0];
    } else {
      this.firebaseApp = initializeApp(environment.firebase);
    }
    this.firebaseAuth = getAuth(this.firebaseApp);
  }

  /**
   * Fluxo completo de autenticacao Apple via Firebase:
   * 1. Inicializa Firebase
   * 2. Cria OAuthProvider('apple.com') com scopes
   * 3. signInWithPopup() abre popup Apple
   * 4. Firebase retorna credential com identityToken
   * 5. Retorna Firebase ID Token + dados do usuario
   *
   * NOTA: Apple so retorna email/name na PRIMEIRA autorizacao.
   * O Firebase gerencia nonce e redirect automaticamente.
   */
  signIn(): Observable<{
    identityToken: string;
    authorizationCode: string;
    email: string | null;
    nome: string | null;
  }> {
    return from(this.performAppleSignIn());
  }

  /**
   * Executa o fluxo de sign-in Apple via Firebase OAuthProvider
   */
  private async performAppleSignIn(): Promise<{
    identityToken: string;
    authorizationCode: string;
    email: string | null;
    nome: string | null;
  }> {
    this.initFirebase();

    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');

    const result = await signInWithPopup(this.firebaseAuth!, provider);

    // Extrair credential Apple do resultado
    const credential = OAuthProvider.credentialFromResult(result);
    const identityToken = credential?.idToken || '';
    const accessToken = credential?.accessToken || '';

    // Obter Firebase ID Token (usado pelo backend para verificacao)
    const firebaseIdToken = await result.user.getIdToken();

    // Dados do usuario (email/nome podem vir do Firebase user)
    const email = result.user.email || null;
    const displayName = result.user.displayName || null;

    return {
      identityToken: firebaseIdToken,
      authorizationCode: accessToken,
      email,
      nome: displayName,
    };
  }

  /**
   * Limpa estado do Firebase Auth apos uso
   */
  async signOutFirebase(): Promise<void> {
    if (this.firebaseAuth) {
      await signOut(this.firebaseAuth);
    }
  }
}
